import React, { useEffect, useState } from 'react';
import { CheckCircle2, Copy, ExternalLink, Link2, Mail, RefreshCw, SendHorizontal } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'border border-amber-400/20 bg-amber-500/10 text-amber-200',
  used: 'border border-emerald-400/20 bg-emerald-500/10 text-emerald-200',
  expired: 'border border-rose-400/20 bg-rose-500/10 text-rose-200',
};

export const AuthorizationLinksHub = ({ authFetch }) => {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    client_email: '',
    providers: ['whatsapp_business'],
    delivery_channel: 'manual',
    expires_in_hours: 72,
    label: '',
  });

  const loadHub = async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/admin/auth-links`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar el módulo de autorizaciones');
      setHub(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar el módulo de autorizaciones');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHub();
  }, []);

  const toggleProvider = (providerKey) => {
    setForm((prev) => {
      const hasProvider = prev.providers.includes(providerKey);
      return {
        ...prev,
        providers: hasProvider ? prev.providers.filter((item) => item !== providerKey) : [...prev.providers, providerKey],
      };
    });
  };

  const createLink = async (event) => {
    event.preventDefault();
    try {
      setCreating(true);
      const res = await authFetch(`${API}/auth-links/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client_email: form.client_email,
          providers: form.providers,
          delivery_channel: form.delivery_channel,
          expires_in_hours: Number(form.expires_in_hours),
          label: form.label || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo crear el enlace');
      toast.success('Enlace de autorización creado');
      setForm((prev) => ({ ...prev, client_email: '', label: '' }));
      await loadHub();
    } catch (error) {
      toast.error(error.message || 'No se pudo crear el enlace');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace copiado');
    } catch {
      toast.error('No se pudo copiar el enlace');
    }
  };

  if (loading && !hub) {
    return <div className="glass-card p-8 text-center text-text-muted" data-testid="authorization-links-hub-loading">Cargando autorizaciones por enlace...</div>;
  }

  if (!hub) return null;

  return (
    <div className="space-y-6" data-testid="authorization-links-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="authorization-links-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Autorización por enlace</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">WhatsApp / email sin pedir claves al cliente</p>
            <h4 className="mt-4 text-3xl font-semibold text-text-heading">Genera un enlace único para que el cliente conecte sus herramientas en lenguaje simple.</h4>
            <p className="mt-4 max-w-3xl text-sm text-text-body">Este es el siguiente paso natural sobre Nango: en vez de explicar OAuth, enviamos un enlace personal, con expiración y trazabilidad de consentimiento.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="authorization-links-hero-chips">
              <span className="insight-chip">Enlace único</span>
              <span className="insight-chip">Consentimiento trazable</span>
              <span className="insight-chip">Sin contraseñas</span>
              <span className="insight-chip">Preparado para WhatsApp y email</span>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="glass-card p-5" data-testid="authorization-links-metric-total">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Total</p>
              <p className="mt-3 text-3xl font-bold text-text-heading">{hub.metrics?.total || 0}</p>
              <p className="mt-2 text-xs text-text-muted">enlaces generados</p>
            </div>
            <div className="glass-card p-5" data-testid="authorization-links-metric-pending">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Pendientes</p>
              <p className="mt-3 text-3xl font-bold text-text-heading">{hub.metrics?.pending || 0}</p>
              <p className="mt-2 text-xs text-text-muted">esperando autorización</p>
            </div>
            <div className="glass-card p-5" data-testid="authorization-links-metric-used">
              <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Completados</p>
              <p className="mt-3 text-3xl font-bold text-text-heading">{hub.metrics?.used || 0}</p>
              <p className="mt-2 text-xs text-text-muted">listos para operar</p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={createLink} className="glass-card p-6" data-testid="authorization-links-create-form">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Nuevo enlace</p>
            <h5 className="mt-2 text-lg font-semibold text-text-heading">Crear autorización para un cliente</h5>
          </div>
          <button type="button" className="btn-secondary" onClick={loadHub} data-testid="authorization-links-refresh-button"><RefreshCw className="h-4 w-4" /> Refrescar</button>
        </div>
        <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <input className="input-kinesthetic" placeholder="Email del cliente" value={form.client_email} onChange={(e) => setForm((prev) => ({ ...prev, client_email: e.target.value }))} data-testid="authorization-links-client-email-input" />
          <input className="input-kinesthetic" placeholder="Etiqueta interna (opcional)" value={form.label} onChange={(e) => setForm((prev) => ({ ...prev, label: e.target.value }))} data-testid="authorization-links-label-input" />
          <select className="input-kinesthetic" value={form.delivery_channel} onChange={(e) => setForm((prev) => ({ ...prev, delivery_channel: e.target.value }))} data-testid="authorization-links-channel-select">
            <option value="manual">Manual</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="email">Email</option>
            <option value="both">Ambos</option>
          </select>
          <input type="number" min="1" max="168" className="input-kinesthetic" value={form.expires_in_hours} onChange={(e) => setForm((prev) => ({ ...prev, expires_in_hours: e.target.value }))} data-testid="authorization-links-expiry-input" />
        </div>
        <div className="mt-5 rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid="authorization-links-providers-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Providers incluidos</p>
          <div className="mt-4 flex flex-wrap gap-3">
            {(hub.available_providers || []).map((provider) => (
              <label key={provider.provider} className="inline-flex items-center gap-2 rounded-full border border-border-subtle px-3 py-2 text-sm text-text-body" data-testid={`authorization-links-provider-option-${provider.provider}`}>
                <input type="checkbox" checked={form.providers.includes(provider.provider)} onChange={() => toggleProvider(provider.provider)} data-testid={`authorization-links-provider-checkbox-${provider.provider}`} />
                {provider.name}
                {provider.coming_soon && <span className="text-[10px] uppercase tracking-[0.16em] text-amber-200">planificado</span>}
              </label>
            ))}
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <button type="submit" className="btn-primary" disabled={creating} data-testid="authorization-links-submit-button"><SendHorizontal className="h-4 w-4" /> {creating ? 'Creando...' : 'Crear enlace'}</button>
        </div>
      </form>

      <div className="grid gap-4 xl:grid-cols-[1.05fr_0.95fr]" data-testid="authorization-links-bottom-grid">
        <div className="glass-card p-6" data-testid="authorization-links-list-card">
          <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Enlaces recientes</p>
          <h5 className="mt-2 text-lg font-semibold text-text-heading">Historial de autorizaciones</h5>
          <div className="mt-4 space-y-3">
            {(hub.auth_links || []).length === 0 && <p className="text-sm text-text-body" data-testid="authorization-links-empty-state">Todavía no hay enlaces generados.</p>}
            {(hub.auth_links || []).map((item) => (
              <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`authorization-link-card-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{item.client_name || item.client_email}</p>
                    <p className="mt-1 text-xs text-text-muted">{item.client_email} · expira {new Date(item.expires_at).toLocaleString('es-ES')}</p>
                  </div>
                  <span className={`rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.16em] ${STATUS_STYLES[item.status] || STATUS_STYLES.pending}`}>{item.status}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`authorization-link-providers-${item.id}`}>
                  {(item.providers || []).map((provider) => <span key={`${item.id}-${provider}`} className="rounded-full border border-border-subtle px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-text-muted">{provider}</span>)}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" className="btn-secondary" onClick={() => copyLink(item.url)} data-testid={`authorization-link-copy-${item.id}`}><Copy className="h-4 w-4" /> Copiar</button>
                  <button type="button" className="btn-secondary" onClick={() => window.open(item.url, '_blank', 'noopener,noreferrer')} data-testid={`authorization-link-open-${item.id}`}><ExternalLink className="h-4 w-4" /> Abrir</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass-card p-6" data-testid="authorization-links-consent-log-card">
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Consentimiento</p>
            <h5 className="mt-2 text-lg font-semibold text-text-heading">Últimos eventos de consentimiento</h5>
            <div className="mt-4 space-y-3">
              {(hub.consent_logs || []).length === 0 && <p className="text-sm text-text-body" data-testid="authorization-links-empty-consent-log">Aún no hay eventos de consentimiento.</p>}
              {(hub.consent_logs || []).slice(0, 8).map((item) => (
                <div key={item.id} className="rounded-[22px] border border-border-subtle bg-background/55 p-4" data-testid={`authorization-consent-log-${item.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm font-semibold text-text-heading">{item.provider}</p>
                      <p className="mt-1 text-xs text-text-muted">{item.result} · {new Date(item.timestamp).toLocaleString('es-ES')}</p>
                    </div>
                    {item.result === 'authorized' ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <Mail className="h-4 w-4 text-amber-200" />}
                  </div>
                  <p className="mt-2 text-xs text-text-body line-clamp-3">{item.consent_text_snapshot}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6" data-testid="authorization-links-pattern-card">
            <div className="flex items-center gap-3 text-text-heading"><Link2 className="h-5 w-5 text-primary" /><h5 className="text-lg font-semibold">Viabilidad e integración</h5></div>
            <div className="mt-4 space-y-3 text-sm text-text-body">
              <p>Este patrón encaja muy bien con Neuralia369 porque reutiliza el Hub de Conexiones, mantiene la misma auditoría y abre un onboarding mucho más simple para cliente final.</p>
              <p>Fase natural siguiente: automatizar el envío del enlace por WhatsApp y email cuando se cierre una venta o cuando el equipo marque “listo para conectar”.</p>
              <p>Para contratos, la ruta más segura hoy es mantener SignWell como producción inmediata y dejar la firma OTP nativa como fase posterior tras revisión legal.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorizationLinksHub;